import React, { useState, useEffect } from 'react';
import { Button, Alert } from '@/components/ui';
import { workIdService, type WorkIdDetail } from '../../../services/workIdService';
import ResponsiveModal from '../../../components/ResponsiveModal';

interface WorkIdUnassignModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdDetail | null;
  onSuccess: () => void;
}

const WorkIdUnassignModal: React.FC<WorkIdUnassignModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible && workId) {
      setError(null);
    }
  }, [visible, workId]);

  // A Work ID has at most one linked task.
  const taskToUnassign = workId?.task ?? null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workId || !taskToUnassign) return;

    setError(null);
    setSubmitting(true);

    try {
      await workIdService.unassign(workId.id, taskToUnassign.id);
      onClose();
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove Work ID from task');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workId) return null;

  const canSubmit = taskToUnassign != null;

  const title = `Remove Work ID from Task - ${workId.workIdentificationId ?? workId.id}`;

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={title}
      size="medium"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="warning"
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
          >
            {submitting ? 'Removing…' : 'Remove from Task'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <p className="tw-text-content-muted tw-mb-3">
          Removing this Work ID from the task will return the Work ID to <strong>Approved</strong>{' '}
          status. The task will no longer be linked to this Work ID.
        </p>

        {taskToUnassign ? (
          <p className="tw-mb-0">
            Remove Work ID from task{' '}
            <strong>
              {taskToUnassign.taskNumber ?? taskToUnassign.id} ({taskToUnassign.type ?? 'Task'})
            </strong>
            ?
          </p>
        ) : (
          <p className="tw-text-content-muted tw-mb-0">No linked tasks to unassign.</p>
        )}
      </form>
    </ResponsiveModal>
  );
};

export default WorkIdUnassignModal;
